'use client';

import * as React from 'react';

import { Icon, type IconName } from '@/hen-ui/atomics/Icon';
import { List, ListItem } from '@/hen-ui/atomics/List';
import { Separator } from '@/hen-ui/atomics/Separator';
import { Text } from '@/hen-ui/atomics/Text';
import { Flex } from '@/hen-ui/composites/Flex';
import { COMPONENT_IDS } from '@/hen-ui/catalog/component-ids';
import { cn } from '@/hen-ui/utils';

import {
  calloutIconVariants,
  calloutLabelVariants,
  calloutVariants,
  type CalloutStyle,
} from './callout.variants';

export type CalloutItem =
  | string
  | {
      text: React.ReactNode;
      /** Optional annotations rendered after this item’s text when `showHighlights` is true. */
      highlights?: React.ReactNode;
    };

export type CalloutState = 'loading' | 'empty' | 'error' | 'ready';

type CalloutDivProps = Omit<
  React.HTMLAttributes<HTMLDivElement>,
  'children' | 'style'
>;

type CalloutSharedProps = CalloutDivProps & {
  /** Uppercase header label (CSS uppercases; consumer may pass any casing). */
  label: string;
  /** Leading icon. Defaults to `sparkles`. */
  icon?: IconName;
  /** Optional operator control (e.g. refresh). Layout only — no domain semantics. */
  action?: React.ReactNode;
  /** Where `action` renders. Defaults to `top` (header trailing). */
  actionPosition?: 'top' | 'bottom';
  /**
   * Surface + accent tokens (same prop name intent as `InsightCard` / `DataPoint`).
   * `neutral-*` variants keep a white surface and carry the accent on the strip,
   * icon chip, and list markers.
   * Not the React inline `style` attribute — use `className` for one-off layout tweaks.
   */
  style?: CalloutStyle;
  /** Opaque meta line (consumer formats attribution / time). */
  footer?: React.ReactNode;
  /**
   * Optional body shown when `state="error"`. Defaults to
   * `"Unable to load content."`. Hosts own localization / soft-fail copy.
   */
  errorContent?: React.ReactNode;
  /**
   * When `false`, hides prose `highlights` and per-item `highlights`.
   * Defaults to `true`.
   */
  showHighlights?: boolean;
  /** Presentational status chrome. Defaults to `ready`. */
  state?: CalloutState;
};

/**
 * Use `body` (prose), `items` (numbered list), or `children` — not more than one.
 */
export type CalloutProps = CalloutSharedProps &
  (
    | {
        body: React.ReactNode;
        /** Annotations after `body` when `showHighlights` is true. */
        highlights?: React.ReactNode;
        items?: never;
        children?: never;
      }
    | {
        items: CalloutItem[];
        body?: never;
        highlights?: never;
        children?: never;
      }
    | {
        children: React.ReactNode;
        body?: never;
        highlights?: never;
        items?: never;
      }
    | {
        /** Chrome-only mode: requires a non-ready `state` (loading / empty / error). */
        state: Exclude<CalloutState, 'ready'>;
        body?: never;
        highlights?: never;
        items?: never;
        children?: never;
      }
  );

function normalizeItem(item: CalloutItem): {
  text: React.ReactNode;
  highlights?: React.ReactNode;
} {
  if (typeof item === 'string') {
    return { text: item };
  }
  return item;
}

function CalloutLoadingBody() {
  return (
    <Flex
      direction='column'
      gap='xs'
      align='stretch'
      className='w-full'
      aria-hidden
    >
      <div className='h-3 w-full animate-pulse rounded-sm bg-neutral-200/80' />
      <div className='h-3 w-11/12 animate-pulse rounded-sm bg-neutral-200/80' />
      <div className='h-3 w-4/5 animate-pulse rounded-sm bg-neutral-200/80' />
    </Flex>
  );
}

export const Callout = React.forwardRef<HTMLDivElement, CalloutProps>(
  (
    {
      label,
      icon = 'sparkles',
      action,
      actionPosition = 'top',
      style: surfaceStyle = 'primary',
      footer,
      errorContent,
      showHighlights = true,
      state = 'ready',
      body,
      highlights,
      items,
      children,
      className,
      role,
      ...rest
    },
    ref,
  ) => {
    const visibleChildren = React.Children.toArray(children).filter(
      (child) => child != null && typeof child !== 'boolean',
    );
    const hasChildren = visibleChildren.length > 0;
    const hasBody = body != null;
    const hasItems = Array.isArray(items) && items.length > 0;
    const bodyModeCount =
      Number(hasBody) + Number(hasItems) + Number(hasChildren);
    const warnedInvalidBodyRef = React.useRef(false);

    if (
      process.env.NODE_ENV !== 'production' &&
      bodyModeCount > 1 &&
      !warnedInvalidBodyRef.current
    ) {
      warnedInvalidBodyRef.current = true;
      console.warn(
        '[hen-ui/Callout] Use only one of `body`, `items`, or `children`. Preferring `children`, then `items`, then `body`.',
      );
    }

    const {
      'aria-label': ariaLabel,
      'aria-labelledby': ariaLabelledBy,
      ...divRest
    } = rest;

    const labelId = React.useId();
    const resolvedLabelledBy =
      ariaLabelledBy ??
      (typeof ariaLabel === 'string' && ariaLabel.trim() !== ''
        ? undefined
        : labelId);
    const showActionTop = action != null && actionPosition === 'top';
    const showActionBottom = action != null && actionPosition === 'bottom';

    const renderReadyBody = () => {
      if (hasChildren) {
        return <>{visibleChildren}</>;
      }

      if (hasItems && items) {
        return (
          <List
            variant='ordered'
            bulletStyle='number'
            markerColor={
              surfaceStyle.includes('secondary') ? 'secondary' : 'primary'
            }
            className='gap-sm'
          >
            {items.map((raw, index) => {
              const item = normalizeItem(raw);
              const key = `callout-item-${index}`;
              return (
                <ListItem key={key} className='gap-sm'>
                  <Flex
                    asChild
                    direction='row'
                    align='center'
                    gap='xs'
                    wrap='wrap'
                    className='min-w-0'
                  >
                    <span>
                      <span className='sr-only'>{index + 1}. </span>
                      <Text variant='b5' tag='span' className='min-w-0'>
                        {item.text}
                      </Text>
                      {showHighlights && item.highlights != null ? (
                        <Flex
                          asChild
                          direction='row'
                          align='center'
                          gap='xxs'
                          wrap='wrap'
                        >
                          <span>{item.highlights}</span>
                        </Flex>
                      ) : null}
                    </span>
                  </Flex>
                </ListItem>
              );
            })}
          </List>
        );
      }

      if (hasBody) {
        return (
          <Flex direction='column' gap='xs' align='stretch' className='min-w-0'>
            <Text variant='b5' tag='div' className='min-w-0'>
              {body}
            </Text>
            {showHighlights && highlights != null ? (
              <Flex direction='row' gap='xxs' wrap='wrap'>
                {highlights}
              </Flex>
            ) : null}
          </Flex>
        );
      }

      return null;
    };

    const renderBody = () => {
      if (state === 'loading') {
        return <CalloutLoadingBody />;
      }
      if (state === 'empty') {
        return null;
      }
      if (state === 'error') {
        return (
          <Text variant='b5' color='subtext' tag='div'>
            {errorContent ?? 'Unable to load content.'}
          </Text>
        );
      }
      return renderReadyBody();
    };

    return (
      <div
        ref={ref}
        data-hen-id={COMPONENT_IDS.Callout}
        data-state={state}
        className={cn(calloutVariants({ style: surfaceStyle }), className)}
        role={role ?? 'region'}
        aria-label={ariaLabel}
        aria-labelledby={resolvedLabelledBy}
        aria-busy={state === 'loading' || undefined}
        {...divRest}
      >
        <Flex
          direction='row'
          align='center'
          justify='between'
          gap='sm'
          className='w-full min-w-0'
        >
          <Flex direction='row' align='center' gap='xs' className='min-w-0'>
            <Flex
              asChild
              direction='row'
              align='center'
              justify='center'
              gap='none'
              className={calloutIconVariants({ style: surfaceStyle })}
            >
              <span aria-hidden>
                <Icon icon={icon} size='sm' color='white' aria-hidden />
              </span>
            </Flex>
            <Text
              id={labelId}
              variant='l1'
              tag='div'
              className={cn(
                'min-w-0',
                calloutLabelVariants({ style: surfaceStyle }),
              )}
            >
              {label}
            </Text>
          </Flex>
          {showActionTop ? (
            <Flex gap='none' className='shrink-0'>
              {action}
            </Flex>
          ) : null}
        </Flex>

        <Flex className='min-w-0 w-full'>{renderBody()}</Flex>

        {showActionBottom || footer != null ? (
          <Flex direction='column' gap='xs' align='stretch' className='w-full'>
            <Separator className='bg-neutral-200' />
            <Flex
              direction='row'
              align='center'
              justify='between'
              gap='sm'
              className='w-full min-w-0'
            >
              {footer != null ? (
                <Flex gap='none' className='min-w-0 flex-1'>
                  {footer}
                </Flex>
              ) : null}
              {showActionBottom ? (
                <Flex gap='none' className='ml-auto shrink-0'>
                  {action}
                </Flex>
              ) : null}
            </Flex>
          </Flex>
        ) : null}
      </div>
    );
  },
);

Callout.displayName = 'Callout';
